import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, IconButton, Button, Chip, Backdrop, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, TextField, Card, Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, Draw as DrawIcon, Clear as ClearIcon, PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import extEquipmentService from '../../../services/extEquipmentService';
import { FORM_TYPES } from './constants';
import AerialLiftForm from './forms/AerialLiftForm';
import CraneForkliftForm from './forms/CraneForkliftForm';
import MaintenanceSafetyForm from './forms/MaintenanceSafetyForm';

const accordionSx = { mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } };
const accordionSummarySx = { minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } };
const sectionTitle = (text) => <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{text}</Typography>;

export default function ExtEquipmentInspectionForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const qrToken = searchParams.get('qrToken');
    const isInitial = searchParams.get('isInitial') === 'true';
    const [uuid] = useState(() => searchParams.get('uuid') || generateUUID());
    const userName = localStorage.getItem('employeeFullName') || '';

    const [tagInfo, setTagInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const [inspectorModalOpen, setInspectorModalOpen] = useState(false);
    const sigPadRef = useRef(null);

    const [form, setForm] = useState({
        uuid,
        qrToken,
        formType: '',
        inspectionDate: dayjs().format('YYYY-MM-DD'),
        isInitial,
        results: {},
        inspectorName: userName,
        signature: '',
        submittedByName: userName,
        status: 'draft',
    });

    const saveTimer = useRef(null);
    const queueSave = (d) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { extEquipmentService.saveInspectionDraft(d).catch(console.error); }, 1000);
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            const existing = await extEquipmentService.loadInspection(uuid);
            if (existing) {
                setForm(existing);
                if (existing.status === 'submitted') setSubmitted(true);
            }
            if (qrToken) {
                try {
                    const info = await extEquipmentService.getInfo(qrToken);
                    setTagInfo(info);
                    if (!existing) {
                        // Auto-fill from tag data
                        const prefill = {};
                        const t = info.tag;
                        if (t?.formType) prefill.formType = t.formType;

                        const prevResults = {};

                        // Aerial Lift fields — map tag unitSubType to form unitType values
                        const subTypeMap = { scissor: 'scissor_lift', boom: 'articulating_boom', manlift: 'man_lift' };
                        if (t?.unitSubType) prevResults.unitType = subTypeMap[t.unitSubType] || t.unitSubType;
                        if (t?.equipmentNumber) prevResults.liftId = t.equipmentNumber;
                        if (t?.location) prevResults.locationOfUse = t.location;
                        if (t?.assignedName) prevResults.operatorPrintName = t.assignedName;

                        // Crane fields (D-27 form)
                        if (t?.owner) prevResults.craneOwner = t.owner;
                        if (t?.serialNumber) prevResults.serialNumber = t.serialNumber;
                        if (t?.manufacturer) prevResults.craneManufacturer = t.manufacturer;
                        if (t?.model) prevResults.model = t.model;
                        if (t?.equipmentNumber) prevResults.equipmentNumber = t.equipmentNumber;

                        // Maintenance/Forklift — equipment# from serial if no equipmentNumber
                        if (!prevResults.equipmentNumber && t?.serialNumber) prevResults.equipmentNumber = t.serialNumber;
                        // Maintenance/Forklift — maintenanceInspector from assigned
                        if (t?.assignedName) prevResults.maintenanceInspector = t.assignedName;

                        // Last inspection date
                        if (info.lastInspectionDate) prevResults.dateOfLastInspection = info.lastInspectionDate;

                        prefill.results = prevResults;
                        setForm(prev => ({ ...prev, ...prefill, results: { ...prev.results, ...prefill.results } }));
                    }
                } catch (e) { console.error('Failed to load tag info:', e); }
            }
            setLoading(false);
        })();
    }, [uuid, qrToken]);

    const handleFieldChange = (field, value) => {
        const u = { ...form, [field]: value };
        setForm(u);
        queueSave(u);
    };

    const handleResultsChange = (results) => {
        const u = { ...form, results };
        setForm(u);
        queueSave(u);
    };

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;
        const u = { ...form, signature: sigPadRef.current.toDataURL() };
        setForm(u);
        queueSave(u);
        setSignatureModalOpen(false);
    };

    const handleSubmit = async () => {
        if (!form.formType) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Form type not set' }); return;
        }

        // Validate all checklist items filled
        const r = form.results || {};
        if (form.formType === 'aerial_lift') {
            const missing = [];
            for (let i = 1; i <= 21; i++) {
                if (!r[`item${i}`]) missing.push(i);
            }
            if (missing.length > 0) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: `Items not filled: ${missing.join(', ')}` }); return;
            }
            if (!r.unitType) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: 'Select Unit Type' }); return;
            }
            if (!r.operatorPrintName) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: 'Operator Print Name required' }); return;
            }
        }

        if (form.formType === 'crane_forklift') {
            const missing = [];
            for (let i = 1; i <= 18; i++) {
                if (!r[`item${i}`]) missing.push(i);
            }
            if (missing.length > 0) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: `Items not filled: ${missing.join(', ')}` }); return;
            }
            if (r.approved === undefined || r.approved === null) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: 'Select APPROVED or REJECTED' }); return;
            }
        }

        if (form.formType === 'maintenance_safety') {
            const msKeys = ['cabProtection','cabWorks','seatBelt','fireExtinguisher','blowOutRadiator','cleanCab','inspectBucket','greaseMovingParts','tireTrackRoller','hornFunctions','coolantOilLevels','inspectBelts','checkLeaks','cleanRollerFrames','windshieldWipers','hydraulicCovers','backupAlarm'];
            const msEmpty = msKeys.filter(k => !r[k]);
            if (msEmpty.length > 0) {
                Swal.fire({ icon: 'error', title: 'Incomplete', text: `${msEmpty.length} checklist items not filled` }); return;
            }
        }

        if (form.formType === 'crane_forklift' && !form.inspectorName) {
            Swal.fire({ icon: 'error', title: 'Incomplete', text: 'Inspected By required' }); return;
        }

        if (!form.signature) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Signature required' }); return;
        }

        const confirm = await Swal.fire({
            title: 'Submit Inspection?', text: 'Once submitted, the form cannot be edited.',
            icon: 'question', showCancelButton: true, confirmButtonText: 'Submit',
        });
        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);
            const submitData = { ...form, status: 'submitted' };
            await extEquipmentService.submitInspection(submitData);
            await extEquipmentService.deleteInspection(uuid);
            Swal.fire({ icon: 'success', title: 'Inspection Submitted!' });
            navigate(`/operations/ext-equipment/profile?qrToken=${qrToken}`);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.error || 'Failed to submit' });
        } finally { setSaving(false); }
    };

    const formTypeConfig = FORM_TYPES.find(f => f.value === form.formType);
    const isReadOnly = submitted;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(-1)} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>
                            {isInitial ? 'Initial Inspection' : 'Monthly Inspection'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {tagInfo?.tag?.label && <Chip label={tagInfo.tag.label} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />}
                        <Chip label={submitted ? 'Submitted' : 'Draft'} size="small"
                            sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 800, mx: 'auto', p: 1 }}>
                    {/* PO Info */}
                    {tagInfo?.tag?.linked && (
                        <Accordion defaultExpanded sx={accordionSx}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                                {sectionTitle(`Equipment: ${tagInfo.tag.linkedDescription || tagInfo.tag.label}`)}
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontSize: '0.8rem' }}>
                                    {tagInfo.tag.equipmentType && <span><strong>Type:</strong> {tagInfo.tag.equipmentType}</span>}
                                    {tagInfo.tag.manufacturer && <span><strong>MFG:</strong> {tagInfo.tag.manufacturer}</span>}
                                    {tagInfo.tag.model && <span><strong>Model:</strong> {tagInfo.tag.model}</span>}
                                    {tagInfo.tag.serialNumber && <span><strong>S/N:</strong> {tagInfo.tag.serialNumber}</span>}
                                    {tagInfo.tag.equipmentNumber && <span><strong>Equip #:</strong> {tagInfo.tag.equipmentNumber}</span>}
                                    {tagInfo.tag.location && <span><strong>Location:</strong> {tagInfo.tag.location}</span>}
                                    {tagInfo.tag.assignedName && <span><strong>Assigned:</strong> {tagInfo.tag.assignedName}</span>}
                                    {tagInfo.tag.assignedPhone && <span><strong>Phone:</strong> <a href={`tel:${tagInfo.tag.assignedPhone}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{tagInfo.tag.assignedPhone}</a></span>}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Inspection Form */}
                    <Accordion defaultExpanded sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {formTypeConfig && <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: formTypeConfig.color }} />}
                                {sectionTitle(formTypeConfig?.label || 'Inspection')}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <TextField label="Inspection Date" type="date" size="small" fullWidth disabled={isReadOnly}
                                InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                                value={form.inspectionDate} onChange={e => handleFieldChange('inspectionDate', e.target.value)} />

                            {form.formType === 'aerial_lift' && <AerialLiftForm results={form.results} onChange={handleResultsChange} isReadOnly={isReadOnly} />}
                            {form.formType === 'crane_forklift' && <CraneForkliftForm results={form.results} onChange={handleResultsChange} isReadOnly={isReadOnly} />}
                            {form.formType === 'maintenance_safety' && <MaintenanceSafetyForm results={form.results} onChange={handleResultsChange} isReadOnly={isReadOnly} inspectionDate={form.inspectionDate} />}
                        </AccordionDetails>
                    </Accordion>

                    {/* Inspected By & Signature — per form type */}
                    <Accordion defaultExpanded sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            {sectionTitle(form.formType === 'crane_forklift' ? 'Inspected By' : 'Signature')}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                {/* Crane/Forklift: "INSPECTED BY" from employee list */}
                                {form.formType === 'crane_forklift' && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField label="Inspected By *" size="small" fullWidth value={form.inspectorName} disabled
                                                InputLabelProps={{ shrink: true }} />
                                            {!isReadOnly && <IconButton onClick={() => setInspectorModalOpen(true)} color="primary" size="small"><PersonSearchIcon /></IconButton>}
                                        </Box>
                                    </Grid>
                                )}

                                {/* Signature */}
                                <Grid item xs={12}>
                                    {form.signature ? (
                                        <Card variant="outlined" sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {form.formType === 'crane_forklift' ? `Inspected by ${form.inspectorName || '—'}` : 'Signature'}
                                                </Typography>
                                                {!isReadOnly && <IconButton size="small" onClick={() => handleFieldChange('signature', '')} color="error"><ClearIcon fontSize="small" /></IconButton>}
                                            </Box>
                                            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5, backgroundColor: '#fff' }}>
                                                <img src={form.signature} alt="Signature" style={{ width: '100%', maxHeight: 100, objectFit: 'contain' }} />
                                            </Box>
                                        </Card>
                                    ) : (
                                        <Button variant="outlined" startIcon={<DrawIcon />} onClick={() => setSignatureModalOpen(true)}
                                            disabled={isReadOnly} fullWidth>Add Signature</Button>
                                    )}
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Submit */}
                    {!submitted && (
                        <Button variant="contained" fullWidth size="large" onClick={handleSubmit} disabled={saving}
                            sx={{ mt: 1, mb: 3, py: 1.5, fontWeight: 700, fontSize: '1rem', backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#43a047' } }}>
                            {saving ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Submit Inspection'}
                        </Button>
                    )}
                </Box>
            </Box>

            <SignatureModalWithAuto open={signatureModalOpen} onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef} onAccept={handleSignatureAccept}
                employeeName={form.operatorName} />
            <SelectSingleEmployeeModal open={operatorModalOpen} onClose={() => setOperatorModalOpen(false)}
                onSelect={(emp) => { handleFieldChange('operatorName', emp.FullName || emp.fullName || ''); setOperatorModalOpen(false); }} />
            <SelectSingleEmployeeModal open={inspectorModalOpen} onClose={() => setInspectorModalOpen(false)}
                onSelect={(emp) => { handleFieldChange('inspectorName', emp.FullName || emp.fullName || ''); setInspectorModalOpen(false); }} />
            <Backdrop open={saving} sx={{ zIndex: 9999 }}><CircularProgress color="inherit" /></Backdrop>
        </Box>
    );
}
